import InfoIcons from '@/icons/InfoIcons'
import MonthRangeSelector, { MonthRange } from '@/components/MonthRangeSelector'
import { REVENUE_QUERY_KEYS } from '@/modules/Dashboard/hooks/useRevenueQueries'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import {
    Bar,
    BarChart,
    LabelList,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts'
import { Skeleton } from '@/components/ui/skeleton'
import dayjs from 'dayjs'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getRevenueTargetsWithActuals } from '@/modules/Dashboard/services'

interface TargetRevenueProps {
    className?: string
    gte?: string
    lte?: string
}

const today = dayjs()

const TargetRevenue: React.FC<TargetRevenueProps> = ({
    className = '',
    gte,
    lte,
}) => {
    const { t } = useTranslation('dashboard')
    // Calculate current quarter
    const currentMonth = today.month() // 0-indexed (0 = January)
    const currentQuarter = Math.floor(currentMonth / 3) // 0, 1, 2, or 3
    const quarterStartMonth = currentQuarter * 3 // 0, 3, 6, or 9

    const [monthRange, setMonthRange] = useState<MonthRange>({
        gte:
            gte ||
            today.month(quarterStartMonth).startOf('month').format('YYYY-MM'),
        lte:
            lte ||
            today
                .month(quarterStartMonth + 2)
                .endOf('month')
                .format('YYYY-MM'),
    })

    // Helper function to safely format numbers
    const safeNumber = (value: any, defaultValue: number = 0): number => {
        if (value === null || value === undefined || isNaN(Number(value))) {
            return defaultValue
        }
        return Number(value)
    }

    // Format number with compact notation (12.3B, 1.5M, etc.)
    const formatCompact = (value: number): string => {
        if (value === 0) return '0'

        const absValue = Math.abs(value)
        let formatted = ''

        if (absValue >= 1_000_000_000) {
            // Billions
            formatted = (value / 1_000_000_000).toFixed(1) + 'B'
        } else if (absValue >= 1_000_000) {
            // Millions
            formatted = (value / 1_000_000).toFixed(1) + 'M'
        } else if (absValue >= 1_000) {
            // Thousands
            formatted = (value / 1_000).toFixed(1) + 'K'
        } else {
            formatted = value.toFixed(1)
        }

        return formatted
    }

    // Fetch real data using React Query with month range
    const { data: revenueData } = useQueryWithHandler({
        queryKey: [
            REVENUE_QUERY_KEYS.targetsWithActuals,
            JSON.stringify(monthRange),
        ],
        queryFn: () =>
            getRevenueTargetsWithActuals({
                gte: monthRange.gte,
                lte: monthRange.lte,
            }),
        loadingComponent: (
            <React.Fragment>
                <div className={`space-y-2 ${className}`}>
                    {/* Header Skeleton */}
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-5 w-40" />
                            <Skeleton className="h-4 w-4 rounded-full" />
                        </div>
                        <Skeleton className="h-8 w-[200px]" />
                    </div>

                    {/* Stats Cards Skeleton */}
                    <div className="grid grid-cols-3 gap-2">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="rounded-lg border bg-card p-4 space-y-2"
                            >
                                <Skeleton className="h-4 w-20" />
                                <Skeleton className="h-8 w-24" />
                            </div>
                        ))}
                    </div>

                    {/* Chart Skeleton */}
                    <div className="rounded-lg border bg-card p-6 space-y-4">
                        <Skeleton className="h-20 w-full" />
                        <div className="flex justify-between items-center px-2">
                            <Skeleton className="h-4 w-8" />
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-4 w-16" />
                        </div>
                    </div>
                </div>
            </React.Fragment>
        ),
        staleTime: 2 * 60 * 1000,
    })

    // Extract summary data
    const summary = useMemo(() => {
        // The response is wrapped: { data: { result: { summary: ... } } }
        const apiResult = (revenueData as any)?.data?.result || revenueData

        if (!apiResult?.summary) {
            return {
                total_target_amount: 0,
                total_actual_amount: 0,
                overall_completion: 0,
            }
        }
        return apiResult.summary
    }, [revenueData])

    // Calculate chart data
    const chartData = useMemo(() => {
        const actual = safeNumber(summary.total_actual_amount)
        const target = safeNumber(summary.total_target_amount)
        const percentage = target > 0 ? Math.round((actual / target) * 100) : 0

        return {
            data: [
                {
                    name: 'Revenue',
                    actual: actual,
                    remaining: Math.max(0, target - actual),
                    target: target,
                    percentage: percentage,
                },
            ],
            maxValue: Math.max(actual, target) * 1.1, // Add 10% padding
        }
    }, [summary])

    // Handle month range change
    const handleMonthRangeChange = (range: MonthRange) => {
        setMonthRange(range)
    }

    return (
        <div className={`space-y-2 ${className}`}>
            {/* Header with Title and Month Range Selector */}
            <div className="flex justify-between items-center flex-col md:flex-row gap-2">
                <div className="flex items-center gap-2">
                    <Typogaphy className="text font-semibold text-primary">
                        {t('target_revenue.title')}
                    </Typogaphy>
                    <InfoIcons className="w-4 h-4 text-primary" />
                </div>
                <div data-tour="revenue-time-selector">
                    <MonthRangeSelector
                        className="w-[100%] md:w-auto"
                        value={monthRange}
                        onChange={handleMonthRangeChange}
                        placement="left"
                    />
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid xs:grid-cols-1 md:grid-cols-3 gap-2">
                <div
                    className="rounded-lg border bg-card p-4"
                    data-tour="revenue-actual"
                >
                    <p className="text-sm text-muted-foreground mb-1">
                        {t('target_revenue.actual')}
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                        {formatCompact(safeNumber(summary.total_actual_amount))}
                    </p>
                </div>
                <div
                    className="rounded-lg border bg-card p-4"
                    data-tour="revenue-target"
                >
                    <p className="text-sm text-muted-foreground mb-1">
                        {t('target_revenue.target')}
                    </p>
                    <p className="text-2xl font-bold text-blue-600">
                        {formatCompact(safeNumber(summary.total_target_amount))}
                    </p>
                </div>
                <div
                    className="rounded-lg border bg-card p-4"
                    data-tour="revenue-completion"
                >
                    <p className="text-sm text-muted-foreground mb-1">
                        {t('target_revenue.completion_percentage')}
                    </p>
                    <p className="text-2xl font-bold">
                        {chartData.data[0].percentage}%
                    </p>
                </div>
            </div>

            {/* Chart */}
            <div className="rounded-lg border bg-card p-6">
                <ResponsiveContainer width="100%" height={80}>
                    <BarChart
                        data={chartData.data}
                        layout="vertical"
                        margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                    >
                        <XAxis
                            type="number"
                            domain={[0, chartData.maxValue]}
                            hide
                        />
                        <YAxis type="category" dataKey="name" hide />
                        <Bar
                            dataKey="actual"
                            stackId="a"
                            fill="hsl(142.1 76.2% 36.3%)"
                            radius={[8, 0, 0, 8]}
                            maxBarSize={40}
                        >
                            <LabelList
                                dataKey="actual"
                                position="center"
                                fill="white"
                                formatter={(value: any) =>
                                    typeof value === 'number'
                                        ? formatCompact(value)
                                        : value
                                }
                                style={{ fontWeight: 600, fontSize: '16px' }}
                            />
                        </Bar>
                        <Bar
                            dataKey="remaining"
                            stackId="a"
                            fill="hsl(215 20.2% 65.1%)"
                            radius={[0, 8, 8, 0]}
                            maxBarSize={40}
                        />
                    </BarChart>
                </ResponsiveContainer>
                <div className="flex justify-between items-center mt-6 px-2">
                    <span className="text-sm text-muted-foreground">0</span>
                    <div className="flex items-center gap-2">
                        <div className="h-3 w-0.5 bg-blue-600"></div>
                        <span className="text-sm font-medium text-blue-600">
                            Target:{' '}
                            {formatCompact(
                                safeNumber(summary.total_target_amount)
                            )}
                        </span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                        {formatCompact(chartData.maxValue)}
                    </span>
                </div>
            </div>
        </div>
    )
}

export default TargetRevenue
